// frontend/src/components/Samples/SampleOrderChainPanel.jsx
// Slide-in panel showing the full order chain (root → current → children)
// with lines per order.

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { sampleOrdersAPI } from '../../api/client';
import { FiX, FiGitBranch, FiExternalLink, FiChevronDown, FiChevronRight, FiFileText } from 'react-icons/fi';
import './SampleOrderChainPanel.css';

// ── Status colours (shared with list) ───────────────────────────────────────
const STATUS_STYLE = {
  OPEN:           { bg:'#dbeafe', color:'#1e40af' },
  RELEASED:       { bg:'#e0f2fe', color:'#0369a1' },
  IN_PROGRESS:    { bg:'#fef3c7', color:'#92400e' },
  SENT_CUSTOMER:  { bg:'#e0f2f1', color:'#00695c' },
  CLOSED:         { bg:'#d1fae5', color:'#065f46' },
  CANCELLED:      { bg:'#fee2e2', color:'#991b1b' },
};
const STATUS_LABEL = {
  OPEN:'Aberto', RELEASED:'Liberto', IN_PROGRESS:'Em Curso',
  SENT_CUSTOMER:'Enviado Cliente', CLOSED:'Fechado', CANCELLED:'Cancelado',
};
const FEEDBACK_STYLE = {
  APPROVED:         { bg:'#d1fae5', color:'#065f46' },
  REJECTED:         { bg:'#fee2e2', color:'#991b1b' },
  APPROVED_CHANGES: { bg:'#fef3c7', color:'#92400e' },
};
const FEEDBACK_LABEL = {
  APPROVED:'Aprovado', REJECTED:'Rejeitado', APPROVED_CHANGES:'Apr. c/ Alt.',
};

const StatusBadge = ({ status, map, labels }) => {
  const s = map[status] || { bg:'#f3f4f6', color:'#6b7280' };
  return (
    <span style={{ fontSize:10, fontWeight:700, padding:'1px 6px', borderRadius:4,
      background:s.bg, color:s.color, whiteSpace:'nowrap' }}>
      {labels[status] || status || '—'}
    </span>
  );
};

// ── Single order card ────────────────────────────────────────────────────────
const OrderCard = ({ order, isCurrent, onNavigate }) => {
  const [expanded, setExpanded] = useState(true);
  const [downloading, setDownloading] = useState(false);

  const handleDownloadPdf = async (e) => {
    e.stopPropagation();
    setDownloading(true);
    try {
      const response = await sampleOrdersAPI.downloadFullPdf(order.public_id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${order.sample_order_no}_report.pdf`;
      document.body.appendChild(link);
      link.click();
      setTimeout(() => { link.remove(); window.URL.revokeObjectURL(url); }, 100);
    } catch (err) {
      console.error('PDF download error:', err);
    } finally {
      setDownloading(false);
    }
  };
  const hasLines = order.lines?.length > 0;
  // Group lines by matrix_line_no, show only SIZE lines (not header rows)
  const displayLines = (order.lines || []).filter(l => l.line_type !== 'HEADER');

  return (
    <div className={`soc-card ${isCurrent ? 'soc-card--current' : ''}`}>
      {/* Card header */}
      <div className="soc-card-header" onClick={() => hasLines && setExpanded(e => !e)}>
        <div className="soc-card-left">
          {hasLines && (
            <span className="soc-expand-icon">
              {expanded ? <FiChevronDown size={14}/> : <FiChevronRight size={14}/>}
            </span>
          )}
          <div className="soc-order-info">
            <span className="soc-order-no">{order.sample_order_no}</span>
            {order.order_type_name && (
              <span className="soc-order-type">{order.order_type_name}</span>
            )}
            {isCurrent && <span className="soc-current-badge">atual</span>}
          </div>
        </div>
        <div className="soc-card-right">
          <StatusBadge status={order.status} map={STATUS_STYLE} labels={STATUS_LABEL}/>
          <button type="button" className="soc-open-btn" title="Gerar relatório PDF"
            onClick={handleDownloadPdf} disabled={downloading}>
            <FiFileText size={13}/>
          </button>
          <button type="button" className="soc-open-btn" title="Abrir pedido"
            onClick={e => { e.stopPropagation(); onNavigate(order.public_id); }}>
            <FiExternalLink size={13}/>
          </button>
        </div>
      </div>

      {/* Lines table */}
      {expanded && hasLines && (
        <div className="soc-lines">
          <table className="soc-lines-table">
            <thead>
              <tr>
                <th>Linha</th>
                <th>Cód. Produto</th>
                <th>Cor</th>
                <th>Cód. ERP</th>
                <th>Cor ERP</th>
                <th>Estado</th>
                <th>Feedback</th>
              </tr>
            </thead>
            <tbody>
              {displayLines.map((l, i) => (
                <tr key={i}>
                  <td className="soc-line-no">{l.matrix_line_no}</td>
                  <td title={l.entered_product_code}>{l.entered_product_code || '—'}</td>
                  <td title={l.entered_product_color}>{l.entered_product_color || '—'}</td>
                  <td title={l.erp_product_code}>{l.erp_product_code || '—'}</td>
                  <td title={l.erp_product_color_code}>{l.erp_product_color_code || '—'}</td>
                  <td><StatusBadge status={l.line_status} map={STATUS_STYLE} labels={STATUS_LABEL}/></td>
                  <td>
                    {l.customer_feedback
                      ? <StatusBadge status={l.customer_feedback} map={FEEDBACK_STYLE} labels={FEEDBACK_LABEL}/>
                      : <span style={{color:'var(--gray-300)'}}>—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ── Main panel ───────────────────────────────────────────────────────────────
const SampleOrderChainPanel = ({ isOpen, onClose, publicId }) => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [currentId, setCurrentId] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOpen || !publicId) return;
    setLoading(true);
    sampleOrdersAPI.getOrderChain(publicId)
      .then(r => {
        if (r.data.success) {
          setOrders(r.data.data);
          setCurrentId(r.data.currentId);
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [isOpen, publicId]);

  if (!isOpen) return null;

  // Sort: roots first (no parent), then by parent chain
  const sorted = [...orders].sort((a, b) => {
    if (!a.parent_sample_order_id && b.parent_sample_order_id) return -1;
    if (a.parent_sample_order_id && !b.parent_sample_order_id) return 1;
    return (a.sample_order_id - b.sample_order_id);
  });

  const handleNavigate = (pid) => {
    onClose();
    navigate(`/samples/orders/${pid}`);
  };

  return (
    <>
      <div className="soc-overlay" onClick={onClose}/>
      <div className="soc-panel">
        <div className="soc-header">
          <div className="soc-header-title">
            <FiGitBranch size={18}/>
            <h3>Sequência de Pedidos</h3>
            <span className="soc-count">{orders.length}</span>
          </div>
          <button onClick={onClose} className="soc-close-btn"><FiX size={18}/></button>
        </div>

        <div className="soc-content">
          {loading ? (
            <div className="soc-loading">A carregar…</div>
          ) : sorted.length === 0 ? (
            <div className="soc-empty">Nenhum pedido relacionado encontrado.</div>
          ) : (
            <div className="soc-list">
              {sorted.map((order, i) => (
                <React.Fragment key={order.sample_order_id}>
                  {i > 0 && (
                    <div className="soc-connector">
                      <span className="soc-connector-line"/>
                      <span className="soc-connector-label">
                        {order.parent_sample_order_id ? '↳ derivado de' : ''}
                      </span>
                    </div>
                  )}
                  <OrderCard
                    order={order}
                    isCurrent={order.sample_order_id === currentId}
                    onNavigate={handleNavigate}
                  />
                </React.Fragment>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default SampleOrderChainPanel;